<ng-container *ngFor="let viewType of viewTypes">
    <ng-container [ngSwitch]="viewType">

        <!-- BaseViewFieldType.Text -->
        <div *ngSwitchCase="BaseViewFieldType.Text" class="view-field">
            <h4 *ngIf="field.label && field.label.trim()" class="p-d-inline">{{ field.label }}: </h4>
            
            <!-- Abbreviated Metadata Display -->
            <div *ngIf="field.isAbbreviatedMetadata" class="abbreviated-metadata-container">
                <div class="metadata-content" [class.expanded]="isExpanded">
                    <pre class="metadata-text">{{ getDisplayText() }}</pre>
                    <button 
                        *ngIf="shouldShowToggle()" 
                        class="toggle-button" 
                        (click)="toggleExpanded()"
                        type="button">
                        {{ isExpanded ? 'Show Less' : 'Show More' }}
                    </button>
                </div>
            </div>
            
            <!-- Regular Text Display -->
            <span *ngIf="!field.isAbbreviatedMetadata">{{ getNestedValue(record, field.key) }}</span>
        </div>

        <!-- BaseViewFieldType.Calendar -->
        <div *ngSwitchCase="BaseViewFieldType.Calendar" class="view-field">
            <div>
                <h4 class="p-d-inline">{{ field.label }}: </h4> {{ getNestedValue(record, field.key) | officeTime: 'short' }}
            </div>
            <div *ngIf="record[field.key] && !checkTimezone">
                <div>
                    <h5 class="p-d-inline">Local Time: </h5><small>{{ localTime(getNestedValue(record, field.key))  }}</small>
                </div>
            </div>
        </div>

        <!-- BaseViewFieldType.Link -->
        <div *ngSwitchCase="BaseViewFieldType.Link" class="view-field">
            <h4 class="p-d-inline">{{ field.label }}: </h4>
            <a *ngIf="!field.sameValueId && getNestedValue(record, field.key)" 
               [href]="'/' + field.controller + '/' + getNestedValue(record, field.key).id" 
               [target]="field.options?.openNewTab ? '_blank' : '_self'"
               [rel]="field.options?.openNewTab ? 'noopener noreferrer' : ''"
               style="color: var(--primary-color)">
                {{ getNestedValue(record, field.key).name }}({{ getNestedValue(record, field.key).id }})
            </a>
            <a *ngIf="field.sameValueId && getNestedValue(record, field.key)" 
               [href]="'/' + field.controller + '/' + getNestedValue(record, field.key)" 
               [target]="field.options?.openNewTab ? '_blank' : '_self'"
               [rel]="field.options?.openNewTab ? 'noopener noreferrer' : ''"
               style="color: var(--primary-color)">
                {{ getNestedValue(record, field.key) }}
            </a>
        </div>

        <!-- BaseViewFieldType.GridLink -->
        <div *ngSwitchCase="BaseViewFieldType.GridLink" class="view-field">
            <h4 class="p-d-inline">{{ field.label }}: </h4>
            <a *ngIf="getNestedValue(record, field.key)" [href]="'/' + field.controller + '/' + record.id" style="color: var(--primary-color)">
                {{ getNestedValue(record, field.key) }}({{ record.id }})
            </a>
        </div>

        <!-- BaseViewFieldType.NameLink -->
        <div *ngSwitchCase="BaseViewFieldType.NameLink" class="view-field">
            <h4 class="p-d-inline">{{ field.label }}: </h4>
            <a *ngIf="getNestedValue(record, field.key)?.[0]" [href]="'/' + field.controller + '/' + getNestedValue(record, field.key)[0].id" style="color: var(--primary-color)">
                {{ getNestedValue(record, field.key)[0].name }}({{ getNestedValue(record, field.key)[0].id }})
            </a>
        </div>

        <!-- BaseViewFieldType.Dropdown -->
        <div *ngSwitchCase="BaseViewFieldType.Dropdown" class="view-field">
            <h4 class="p-d-inline">{{ field.label }}: </h4>
            <span>{{ getDropdownLabel(record, field.key) }}</span>
        </div>
        
        <!-- BaseViewFieldType.MultiSelect -->
        <div *ngSwitchCase="BaseViewFieldType.MultiSelect" class="view-field">
            <h4 class="p-d-inline">{{field.label}}: </h4>
            <ul>
                <li *ngFor="let item of getNestedValue(record, field.key)">
                    <ng-container *ngIf="field.controller === 'external-link'; else internalLink">
                        <a *ngIf="hasValidExternalLink(item)" [href]="getExternalLinkUrl(item)" target="_blank" style="color: var(--primary-color);">{{getExternalLinkText(item)}}</a>
                    </ng-container>
                    <ng-template #internalLink>
                        <ng-container *ngIf="field.controller && field.controller !== 'external-link'; else regularText">
                            <a [href]="'/' + field.controller + '/' + item" style="color: var(--primary-color)">
                                {{item}}
                            </a>
                        </ng-container>
                        <ng-template #regularText>
                            {{item}}
                        </ng-template>
                    </ng-template>
                </li>
            </ul>
        </div>

        <!-- BaseViewFieldType.MultiSelectObj -->
        <div *ngSwitchCase="BaseViewFieldType.MultiSelectObj" class="view-field">
            <h4 class="p-d-inline">{{field.label}}: </h4>
            <ul>
                <li *ngFor="let item of getNestedValue(record, field.key)">
                    {{item.identifier || item[field.optionLabel] || item.name || item.title || item[field.options?.fieldName]}}
                    <a *ngIf="item.id && !field.options?.isDynamicEntity" [href]="'/' + field.controller + '/' + item.id" style="color: var(--primary-color)">
                        ({{item.id}})
                    </a>
                    <a *ngIf="item.id && field.options?.isDynamicEntity" [href]="'/d/' + field.controller + '/view/' + item.id" style="color: var(--primary-color)">
                        ({{item.id}})
                    </a>
                </li>
            </ul>
        </div>

        <!-- BaseViewFieldType.Title -->
        <div *ngSwitchCase="BaseViewFieldType.Title" class="view-field">
            <ng-container [ngSwitch]="field.tagType">
            <h1 *ngSwitchCase="'h1'">{{ field.key ? getNestedValue(record, field.key) : field.label }}</h1>
            <h2 *ngSwitchCase="'h2'">{{ field.key ? getNestedValue(record, field.key) : field.label }}</h2>
            <h3 *ngSwitchCase="'h3'">{{ field.key ? getNestedValue(record, field.key) : field.label }}</h3>
            <h4 *ngSwitchCase="'h4'">{{ field.key ? getNestedValue(record, field.key) : field.label }}</h4>
            <h5 *ngSwitchCase="'h5'">{{ field.key ? getNestedValue(record, field.key) : field.label }}</h5>
            <p *ngSwitchCase="'p'">{{ field.key ? getNestedValue(record, field.key) : field.label }}</p>
            <small *ngSwitchCase="'small'">{{ field.key ? getNestedValue(record, field.key) : field.label }}</small>
            <!-- Default case if tagType is not specified -->
            <p *ngSwitchDefault>{{ field.key ? getNestedValue(record, field.key) : field.label }}</p>
            </ng-container>
        </div>

        <!-- BaseViewFieldType.DropdownWithLabels -->
        <div *ngSwitchCase="BaseViewFieldType.DropdownWithLabels" class="view-field">
            <h4 class="p-d-inline">{{ field.label }}: </h4>
            <span>{{ getLabelForValue(getNestedValue(record, field.key)) }}</span>
        </div>

        <!-- BaseViewFieldType.ColorDisplay -->
        <div *ngSwitchCase="BaseViewFieldType.ColorDisplay" class="view-field">
            <div class="color-display-container">
                <strong>{{ field.label }}:</strong>
                <div class="color-box" [style.background]="getNestedValue(record, field.key)"></div>
                <span class="color-code">{{ getNestedValue(record, field.key) }}</span>
            </div>
        </div>
        
        <!-- BaseViewFieldType.NestedList -->
        <div *ngSwitchCase="BaseViewFieldType.NestedList" class="view-field">
            <app-nested-list-content [field]="field" [record]="record"></app-nested-list-content>
        </div>

        <!-- BaseViewFieldType.Boolean -->
        <div *ngSwitchCase="BaseViewFieldType.Boolean" class="view-field">
            <h4 class="p-d-inline">{{ field.label }}: </h4>
            <span>{{ getBooleanLabel(record[field.key], field.booleanoptions) }}</span>
        </div>
        <!-- BaseViewFieldType.RichText for Rich Text Rendering -->
        <div *ngSwitchCase="BaseViewFieldType.RichText" class="view-field">
            <h4 class="p-d-inline">{{ field.label }}:</h4>
            <div class="p-ml-2" *ngIf="isValidRichText(getNestedValue(record, field.key))" 
                 [innerHTML]="getSafeHtml(getNestedValue(record, field.key))"
                 (mousedown)="handleRichTextClick($event)"></div>
        </div>
        <!-- BaseViewFieldType.TimeTaken -->
        <div *ngSwitchCase="BaseViewFieldType.TimeTaken" class="view-field">
            <h4 class="p-d-inline">{{ field.label }}: </h4>
            <span>
            {{ formatTimeTaken(getNestedValue(record, field.key)) }}
            </span>
        </div>    
        <!-- BaseViewFieldType.Costs -->
        <div *ngSwitchCase="BaseViewFieldType.Costs" class="view-field">
            <h4 class="field-label">{{ field.label }}:</h4>
            <div class="costs-wrapper" *ngIf="record && record[field.key] && record[field.key].length > 0">
              <ng-container *ngFor="let cost of record[field.key]">
                <div class="cost-chip">
                  <img
                    *ngIf="cost?.id?.image_ref?.thumbnail_url_32"
                    [src]="cost.id.image_ref.thumbnail_url_32 + '?ts=' + timestamp"
                    class="currency-icon"
                    alt="currency"
                  />
                  <span class="cost-value">{{ cost.c | number }}</span>
                  <span class="currency-name">{{ cost.id?.name }}</span>
                </div>
              </ng-container>
            </div>
            <div *ngIf="!record || !record[field.key] || record[field.key].length === 0" class="no-costs">
            </div>
        </div>
        <!-- BaseViewFieldType.AnimationTriggerMap -->
        <div *ngSwitchCase="BaseViewFieldType.AnimationTriggerMap" class="view-field">
            <h4 class="p-d-inline">{{ field.label }}: </h4>
            <app-animation-trigger-map-view [field]="field" [record]="record"></app-animation-trigger-map-view>
        </div>
    </ng-container>
</ng-container>
